import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { BadgeModule } from 'primeng/badge';
import { MenuModule } from 'primeng/menu';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { DialogTriggerDirective } from '../../directives/dialog-trigger.directive';
import { Announcement } from '../../models/api/core/announcements/announcement';
import { Action } from '../../models/core/action';
import { WatchlistService } from '../../services/view/watchlist.service';
import { BaseComponent } from '../base/base.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { IconComponent } from '../core/labels/icon.component';
import { LinkLabelComponent } from '../core/labels/link-label.component';
import { TagComponent } from '../core/labels/tag.component';
import { ActionRowComponent } from '../core/panels/action-row.component';
import { DialogComponent } from '../core/panels/dialog.component';
import { DrawerComponent } from '../core/panels/drawer.component';
import { MenuComponent } from '../core/panels/menu.component';
import { AnnouncementsComponent } from './announcements.component';
import { HelpSideDrawerComponent } from './help-side-drawer.component';
import { QuickSearchComponent } from './quick-search.component';
import { SidenavComponent } from './sidenav.component';
@Component({
  selector: 'app-header',
  imports: [
    QuickSearchComponent,
    RouterModule,
    MenuModule,
    MenuComponent,
    IconComponent,
    SidenavComponent,
    DrawerComponent,
    TagComponent,
    BadgeModule,
    OverlayBadgeModule,
    DialogComponent,
    AnnouncementsComponent,
    ActionRowComponent,
    ButtonComponent,
    LinkLabelComponent,
    DialogTriggerDirective,
    HelpSideDrawerComponent,
  ],
  template: `
    @if (user) {
      <header
        class="tw-w-full tw-flex tw-flex-wrap tw-justify-center tw-bg-[var(--sed-grey-20-color)]"
        [class.tw-shadow-lg]="hasScrolled()"
        role="banner"
      >
        <app-drawer #drawer styleClass="mobile-menu">
          <app-sidenav />
        </app-drawer>
        <app-help-side-drawer #helpDrawer />
        <div
          class="tw-max-w-[1300px] tw-flex-col tw-w-full tw-grow tw-min-w-0 tw-box-border"
        >
          <div class="tw-flex tw-min-w-0 tw-p-2 sm:tw-p-6 sm:tw-items-center">
            <div class="tw-block md:tw-hidden">
              <app-link-label class="tw-pr-4" (click)="drawer.visible = true">
                <app-icon
                  name="fa-bars"
                  color="var(--sed-dynamic-black-color)"
                  size="18px"
                  ariaLabel="Side nav toggle icon"
                ></app-icon>
              </app-link-label>
            </div>
            <a
              #logoLink
              routerLink=""
              (click)="logoLink.blur()"
              aria-label="Go to home page"
              class="logo-link tw-pr-4 sm:tw-shrink-0 tw-min-w-0"
            >
              <img
                src="assets/images/omni-logo.png"
                class="logo"
                alt="Sedgwick OmniInsights Logo"
              />
            </a>
            <div
              class="header-toolbar tw-flex tw-min-w-0 tw-flex-1 tw-flex-row tw-items-center tw-justify-between tw-gap-2 tw-pt-2 tw-px-2 sm:tw-px-4"
            >
              <!-- Mobile tab hamburger: visible only below md -->
              <div
                class="tw-flex tw-shrink-0 tw-justify-start md:tw-hidden tw-pr-2 tw-pt-2 tw-relative"
              >
                <button
                  type="button"
                  class="tw-bg-transparent tw-border-none tw-cursor-pointer tw-flex tw-items-center tw-gap-2 tw-text-base"
                  (click)="toggleMobileTabs()"
                  [attr.aria-expanded]="mobileTabsOpen"
                  aria-controls="mobile-tab-menu"
                  aria-label="Navigation menu"
                >
                  <i class="fa-light fa-bars" style="font-size: 1.1em;"></i>
                </button>
                @if (mobileTabsOpen) {
                  <div
                    id="mobile-tab-menu"
                    role="menu"
                    class="tw-absolute tw-top-full tw-left-0 tw-z-50 tw-flex tw-flex-col tw-gap-1 tw-bg-[var(--sed-grey-20-color)] tw-shadow-lg tw-min-w-[160px]"
                  >
                    @for (item of tabs; track item; let i = $index) {
                      @if (item.authorized) {
                        <div
                          class="tab tw-px-4 tw-pb-1 tw-pt-2 hover:tw-underline tw-decoration-2"
                          [routerLink]="item.routerLink"
                          routerLinkActive="tab-active"
                          [routerLinkActiveOptions]="{
                            exact: item.key === 'home',
                          }"
                          role="menuitem"
                          tabindex="0"
                          (click)="itemClick(item); mobileTabsOpen = false"
                          (keydown.enter)="
                            itemClick(item); mobileTabsOpen = false
                          "
                          (keydown.space)="
                            itemClick(item);
                            mobileTabsOpen = false;
                            $event.preventDefault()
                          "
                          [attr.aria-label]="item.label"
                        >
                          {{ item.label }}
                        </div>
                      }
                    }
                  </div>
                }
              </div>
              <div
                class="header-toolbar-end tw-flex tw-min-w-0 tw-flex-1 tw-flex-row tw-flex-wrap tw-items-center tw-justify-end tw-gap-x-2 tw-gap-y-2 tw-pt-2 xl:tw-flex-nowrap"
              >
                @if (showHeaderIcons) {
                  <div
                    class="tw-pr-2 tw-shrink-0"
                    appDialogTrigger
                    (click)="announceDialog.visible = true"
                    (keyup.enter)="announceDialog.visible = true"
                    (keyup.space)="announceDialog.visible = true"
                    [tabindex]="0"
                  >
                    @if (announcements.length) {
                      <div
                        class="tw-flex announcements tw-justify-center tw-items-center tw-font-bold tw-cursor-pointer tw-animate-in tw-zoom-in tw-px-3 tw-py-1"
                      >
                        <div class="tw-pt-1 tw-pr-1">
                          <app-icon
                            name="fa-bullhorn fa-light"
                            size="16px"
                            color="var(--sed-dynamic-black-color)"
                          ></app-icon>
                        </div>
                        <span class="tw-pr-2 tw-hidden md:tw-block"
                          >Announcements</span
                        >
                        <app-tag> {{ announcements.length }}</app-tag>
                      </div>
                    }
                  </div>
                }
                <app-dialog #announceDialog label="Announcements">
                  <ng-template #content>
                    <app-announcements [announcements]="announcements" />
                  </ng-template>
                  <ng-template #footer>
                    <app-action-row class="tw-w-full">
                      <app-button
                        right1
                        (click)="announceDialog.visible = false"
                        label="Close"
                      />
                    </app-action-row>
                  </ng-template>
                </app-dialog>
                @if (
                  showHeaderIcons &&
                  passwordExpiriationCount > 0 &&
                  passwordExpiriationCount <= 14
                ) {
                  <button
                    type="button"
                    class="tw-px-1 tw-bg-transparent tw-border-none tw-cursor-pointer"
                    aria-label="Password expiration warning, expires in {{
                      passwordExpiriationCount
                    }} days"
                    [attr.aria-expanded]="isPasswordWarningExpanded"
                    aria-haspopup="dialog"
                    (click)="onPasswordWarningToggle($event, passwordMenu)"
                    (keydown.enter)="
                      onPasswordWarningToggle($event, passwordMenu)
                    "
                    (keydown.space)="
                      onPasswordWarningToggle($event, passwordMenu);
                      $event.preventDefault()
                    "
                  >
                    @if (
                      passwordExpiriationCount > 0 &&
                      passwordExpiriationCount <= 7
                    ) {
                      <div
                        class="tw-flex tw-flex-full tw-justify-center tw-items-center"
                      >
                        <span
                          class="tw-mb-0.5 fa-layers fa-fw"
                          style="font-size: 28px;"
                        >
                          <i
                            class="tw-mb-0.5 fa-solid fa-triangle-exclamation"
                            style="color: var(--sed-status-red-color);"
                          ></i>
                          <i
                            class="tw-mb-0.5 fa-light fa-triangle-exclamation"
                            style="color: white ;"
                          ></i>
                        </span>
                      </div>
                    } @else if (
                      passwordExpiriationCount > 7 &&
                      passwordExpiriationCount <= 14
                    ) {
                      <div
                        class="tw-flex tw-flex-full tw-justify-center tw-items-center"
                      >
                        <span
                          class="tw-mb-0.5 fa-layers fa-fw"
                          style="font-size: 28px;"
                        >
                          <i
                            class="tw-mb-0.5 fa-solid fa-triangle-exclamation"
                            style="color: var(--sed-status-yellow-color);"
                          ></i>
                          <i
                            class="tw-mb-0.5 fa-light fa-triangle-exclamation"
                            style="color: black;"
                          ></i>
                        </span>
                      </div>
                    }
                  </button>
                }
                <div
                  class="header-quick-search tw-hidden tw-min-w-0 tw-flex-1 tw-basis-0 tw-pl-2 tw-pr-0 max-xl:tw-basis-full max-xl:tw-grow md:tw-block md:tw-min-w-[200px] lg:tw-min-w-[240px] xl:tw-min-w-[280px]"
                >
                  @if (!user.isSpecificClaim && showHeaderIcons) {
                    <app-quick-search />
                  }
                </div>
                @for (item of buttons; track item; let i = $index) {
                  @if (item.authorized) {
                    <a
                      [routerLink]="item.routerLink"
                      [queryParams]="item.queryParams"
                      class="header-link tw-px-2 tw-mx-1"
                      [attr.aria-label]="getButtonAriaLabel(item)"
                      tabindex="0"
                    >
                      <app-icon
                        [name]="item.icon"
                        size="18px"
                        color="var(--sed-dynamic-black-color)"
                        ariaLabel=""
                      ></app-icon>
                    </a>
                  }
                }

                @if (showHeaderIcons) {
                  <button
                    type="button"
                    class="header-link tw-px-2 tw-mx-1 tw-bg-transparent tw-border-none tw-cursor-pointer"
                    aria-label="Open help"
                    (click)="helpDrawer.visible = true"
                    (keydown.enter)="
                      helpDrawer.visible = true; openHelpByKeyboard()
                    "
                    (keydown.space)="
                      helpDrawer.visible = true;
                      openHelpByKeyboard();
                      $event.preventDefault()
                    "
                  >
                    <app-icon
                      name="fa-question fa-light"
                      size="18px"
                      color="var(--sed-dynamic-black-color)"
                      ariaLabel=""
                    ></app-icon>
                  </button>
                }

                <button
                  type="button"
                  class="user-menu-button tw-flex tw-shrink-0 tw-items-center tw-cursor-pointer tw-pt-0 tw-bg-transparent tw-border-none"
                  [attr.aria-label]="
                    user?.firstName +
                    ' menu' +
                    (actions?.length
                      ? ', has ' + actions.length + ' menu items'
                      : '')
                  "
                  [attr.aria-expanded]="isUserMenuExpanded"
                  (click)="onUserMenuToggle($event, menu)"
                  (keydown.enter)="onUserMenuToggle($event, menu)"
                  (keydown.space)="
                    onUserMenuToggle($event, menu); $event.preventDefault()
                  "
                >
                  <app-icon
                    name="fa-user fa-light"
                    size="18px"
                    color="black"
                    ariaLabel=""
                  ></app-icon>

                  <div
                    class="header-user-name tw-pl-3 tw-shrink-0 tw-truncate tw-max-w-[10rem]"
                  >
                    <b>{{ user?.firstName }}</b>
                  </div>
                </button>
                <span class="tw-sr-only" aria-live="polite">{{
                  userMenuStatus
                }}</span>
                <app-menu
                  #menu
                  [items]="actions"
                  appendTo="body"
                  (click)="menuClick($event)"
                  (visibilityChange)="onUserMenuVisibilityChange($event)"
                >
                  <ng-template #header>
                    <span
                      class="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-2 tw-text-[14px] tw-pl-2 tw-pr-4"
                    >
                      <i class="tw-pl-2 fa-user fa-light icon-size"></i>
                      <div class="tw-pl-4">
                        <div class="tw-text-lg">
                          {{ user.firstName }} {{ user.lastName }}
                        </div>
                        <div><b>Username</b>:{{ user.userName }}</div>
                      </div>
                    </span>
                  </ng-template>
                </app-menu>
                <app-menu
                  #passwordMenu
                  [items]="passwordActions"
                  appendTo="body"
                  (click)="menuClick($event)"
                  (visibilityChange)="onPasswordMenuVisibilityChange($event)"
                >
                  <ng-template #header>
                    <span
                      class="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-2 tw-text-[14px] tw-pl-2 tw-pr-4"
                    >
                      <div class="tw-pl-4">
                        <div class="tw-text-lg">Password Notification</div>
                      </div>
                    </span>
                  </ng-template>
                </app-menu>
              </div>
            </div>
          </div>
          <!-- Desktop tab list: hidden below md (scroll + safe alignment so portrait md widths are not clipped) -->
          <div
            class="desktop-tabs tw-hidden md:tw-flex tw-w-full tw-min-w-0 tw-gap-1 tw-px-3 tw-pt-4 sm:tw-px-4"
            role="tablist"
            aria-label="Main navigation tabs"
            aria-orientation="horizontal"
          >
            @for (item of tabs; track item; let i = $index) {
              @if (item.authorized) {
                <div
                  class="tab tw-px-4 tw-pb-1 tw-pt-2 hover:tw-underline tw-decoration-2"
                  [routerLink]="item.routerLink"
                  routerLinkActive="tab-active"
                  [routerLinkActiveOptions]="{ exact: item.key === 'home' }"
                  #rla="routerLinkActive"
                  (keydown.enter)="itemClick(item)"
                  (keydown.space)="itemClick(item); $event.preventDefault()"
                  role="tab"
                  [id]="getTabId(item, i)"
                  [attr.aria-controls]="getTabPanelId(item, i)"
                  [attr.aria-selected]="rla.isActive"
                  tabindex="0"
                  [attr.aria-label]="item.label"
                >
                  <span matBadgeOverlap="false">{{ item.label }}</span>
                </div>
              }
            }
          </div>
          @for (item of tabs; track item; let i = $index) {
            @if (item.authorized) {
              <div
                [id]="getTabPanelId(item, i)"
                role="tabpanel"
                [attr.aria-labelledby]="getTabId(item, i)"
                [attr.aria-hidden]="!isTabActive(item)"
                [class.tw-hidden]="!isTabActive(item)"
              ></div>
            }
          }
        </div>
      </header>
      <div
        id="header-status"
        role="status"
        class="screen-reader-text"
        aria-live="polite"
      ></div>
    }
  `,
  styles: `
    :host {
      display: block;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 1000;
      background-color: var(--sed-grey-20-color);
    }

    .message {
      background-color: var(--color-accent-text);
      color: white;
      font-weight: bold;
    }

    .logo {
      height: 38px;
      width: auto;
      max-width: 100%;
    }

    .logo-link {
      text-decoration: none;
      outline-offset: 2px;
      min-width: 0;
    }

    .logo-link:focus-visible {
      outline: 2px solid var(--sed-sedgwick-black-color);
      border-radius: 4px;
    }

    .header-icons-row {
      flex-wrap: nowrap !important;
    }

    .header-quick-search {
      min-width: 0;
      max-width: 100%;
    }

    /* First name: hide on narrow portrait only; always show from md up and in any landscape */
    .header-user-name {
      display: none;
    }

    @media (min-width: 768px) {
      .header-user-name {
        display: block;
      }
    }

    @media (orientation: landscape) {
      .header-user-name {
        display: block;
      }
    }

    @media (orientation: portrait) and (max-width: 767px) {
      .header-user-name {
        display: none;
      }
    }

    @media (max-width: 768px) {
      .logo {
        height: auto;
        max-height: 38px;
        max-width: 100%;
        padding-right: 0rem !important;
      }

      .logo-link {
        width: 100%;
        padding-right: 0 !important;
      }

      .announcements {
        padding-top: 0.5rem !important;
        padding-bottom: 0.5rem !important;
      }
    }

    .person-icon {
      color: white;
      height: 28px;
      width: 28px;
      font-size: 28px;
    }

    .tab {
      text-transform: none;
      border-bottom: 6px solid transparent;
      cursor: pointer;
      font-size: 18px;
      letter-spacing: 0.01em;
      word-break: normal;
      overflow-wrap: normal;
      white-space: nowrap;
    }

    .desktop-tabs {
      box-sizing: border-box;
      width: 100%;
      max-width: 100%;
      min-width: 0;
      overflow-x: auto;
      overflow-y: hidden;
      padding-bottom: 2px;
      justify-content: center;
      scroll-padding-inline: 0.75rem;
      -webkit-overflow-scrolling: touch;
    }

    /* Portrait tablet widths: left-align so overflowing tabs scroll from the start (331776) */
    @media (orientation: portrait) and (min-width: 768px) and (max-width: 1199px) {
      .desktop-tabs {
        justify-content: flex-start;
      }
    }

    .desktop-tabs .tab {
      flex-shrink: 0;
    }

    .tab-active {
      font-weight: bold;
      border-bottom: 6px solid var(--sed-sedgwick-green-color);
    }

    .icon-size {
      font-size: 2rem;
    }
  `,
})
export class HeaderComponent
  extends BaseComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private watchlist = inject(WatchlistService);
  private el = inject(ElementRef);
  private destroyRef = inject(DestroyRef);
  private headerObserver: ResizeObserver | null = null;

  showMenu = true;
  userMenuStatus = '';
  isUserMenuExpanded = false;
  isPasswordWarningExpanded = false;
  isTabListVertical = false;
  mobileTabsOpen = false;
  hasScrolled = signal(false);
  passwordExpiriationCount = 0;
  passwordMenuLabel = 'Change Password';

  announcements: Announcement[] = [];

  buttons: Action[] = [];

  tabs: Action[] = [];

  passwordActions: Action[] = [];

  actions: Action[] = [];

  /**
   * Hex-only users (NO_VOV_HEX_ONLY) are limited to Dashboards, so the header
   * exposes only the user menu button.
   */
  protected get showHeaderIcons(): boolean {
    return !this.session.authorize('NoVovHexOnly');
  }

  async ngOnInit() {
    // Initialize buttons array
    this.buttons = [
      {
        icon: 'fa-history fa-light',
        routerLink: 'view',
        queryParams: { tab: 'history' },
        authorized: this.showHeaderIcons,
      },
      {
        icon: 'fa-star fa-light',
        routerLink: 'view',
        queryParams: { tab: 'watchlist' },
        authorized: this.showHeaderIcons,
      },
    ];

    if (!this.session.isSpecificClaimUser) {
      this.buttons.unshift({
        icon: 'fa-search fa-light',
        routerLink: 'view',
        queryParams: { tab: 'search' },
        authorized: this.showHeaderIcons,
      });
    }

    this.tabs = [
      {
        label: 'Home',
        routerLink: '',
        key: 'home',
        authorized: this.session.authorize('HomeModule'),
      },
      {
        label: 'Dashboards',
        routerLink: 'dashboards',
        authorized:
          !this.session.isSpecificClaimUser &&
          this.session.authorize('DashboardModule'),
      },
      {
        label: 'View',
        routerLink: 'view',
        authorized: this.session.authorize('ViewModule'),
      },
      {
        label: 'To-do',
        routerLink: 'todo',
        authorized: !this.session.authorize('NoVovHexOnly'),
      },
      {
        label: 'Reports',
        routerLink: 'reports',
        authorized:
          !this.session.isSpecificClaimUser &&
          (this.session.authorize('ReportsModule') ||
            this.session.authorize('FiltersModule') ||
            this.session.authorize('DistributedReports')),
      },
      {
        label: 'Query',
        routerLink: 'query',
        authorized: this.session.authorize('Query'),
      },
      {
        label: 'Alerts',
        routerLink: 'alerts',
        authorized:
          !this.session.isSpecificClaimUser &&
          this.session.authorize('AlertsModule'),
      },
      {
        label: 'Design',
        routerLink: 'design',
        authorized: this.session.user?.qaTools ?? false,
      },
      {
        label: 'QA Tools',
        routerLink: 'qa-tools',
        authorized: this.session.user?.qaTools ?? false,
      },
    ];

    this.api.announcements.get().then((announcement) => {
      this.announcements = announcement;
    });

    this.watchlist.load();

    this.actions = [
      {
        label: 'Preferences',
        icon: 'fa-gear fa-light',
        routerLink: '/preferences',
      },
    ];

    if (!this.session.user?.isSsoUser) {
      this.actions.push({
        label: 'Change password',
        icon: 'fa-key fa-light',
        routerLink: '/password-change',
      });
    }

    this.actions.push({
      label: 'Log out',
      icon: 'fa-right-from-bracket fa-light',
      key: 'logout',
    });

    this.passwordExpiriationCount = this.calculatePasswordExpirationDays();

    if (
      this.passwordExpiriationCount > 0 &&
      this.passwordExpiriationCount <= 14
    ) {
      this.passwordMenuLabel =
        this.passwordExpiriationCount > 0
          ? `Password expires in ${this.passwordExpiriationCount} day${this.passwordExpiriationCount > 1 ? 's' : ''}`
          : 'Password expired';
      this.passwordActions.push({
        label: this.passwordMenuLabel,
        routerLink: '/password-change',
      });
    }

    this.checkTabListOrientation();
    this.updateScrollState();
  }

  openHelpByKeyboard(): void {
    setTimeout(() => {
      (
        document.querySelector('.help-side-drawer button') as HTMLElement
      )?.focus();
    }, 300);
  }

  calculatePasswordExpirationDays(): number {
    if (!this.session.user?.passwordExpireDate) {
      return 0;
    }

    const expireDate = new Date(this.session.user.passwordExpireDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expireDate.setHours(0, 0, 0, 0);

    const diffTime = expireDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  }

  ngAfterViewInit(): void {
    const host = this.el.nativeElement as HTMLElement;
    this.headerObserver = new ResizeObserver(() => {
      document.documentElement.style.setProperty(
        '--header-height',
        `${host.offsetHeight}px`,
      );
    });
    this.headerObserver.observe(host);
    this.destroyRef.onDestroy(() => this.headerObserver?.disconnect());
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.updateScrollState();
  }

  private updateScrollState(): void {
    this.hasScrolled.set(window.scrollY > 0);
  }

  override ngOnDestroy(): void {
    document.removeEventListener('click', this.closeMobileTabsOnOutsideClick);
    super.ngOnDestroy();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkTabListOrientation();
  }

  private checkTabListOrientation(): void {
    // Tailwind's md breakpoint is 768px
    // Below 768px: vertical (no flex, items stack)
    // At or above 768px: horizontal (flex, items in a row)
    this.isTabListVertical = window.innerWidth < 768;
  }

  toggleMenu() {
    this.showMenu = !this.showMenu;
  }

  toggleMobileTabs(): void {
    this.mobileTabsOpen = !this.mobileTabsOpen;
    if (this.mobileTabsOpen) {
      setTimeout(() => {
        document.addEventListener('click', this.closeMobileTabsOnOutsideClick);
      });
    }
  }

  private closeMobileTabsOnOutsideClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const menu = document.getElementById('mobile-tab-menu');
    if (menu && !menu.contains(target)) {
      this.mobileTabsOpen = false;
      document.removeEventListener('click', this.closeMobileTabsOnOutsideClick);
    }
  };

  menuClick(action: Action) {
    if (action.key === 'logout') {
      this.session.logout();
      return;
    }

    if (action.routerLink) {
      this.router.navigate([action.routerLink], {
        queryParams: action.queryParams,
      });
    }
  }

  onPasswordWarningToggle(event: Event, menu: MenuComponent) {
    menu.toggle(event);
  }

  onPasswordMenuVisibilityChange(isExpanded: boolean) {
    this.isPasswordWarningExpanded = isExpanded;
  }

  onUserMenuToggle(event: Event, menu: MenuComponent) {
    menu.toggle(event);
  }

  onUserMenuVisibilityChange(isExpanded: boolean) {
    this.isUserMenuExpanded = isExpanded;
    this.userMenuStatus = isExpanded ? 'menu expanded' : 'menu collapsed';
  }

  itemClick(item: Action) {
    this.router.navigate([item.routerLink]);
    document.getElementById('header-status').textContent =
      `Tab ${item.label} is open`;
  }

  getButtonAriaLabel(item: Action): string {
    const tabName = item.queryParams?.['tab'];
    if (tabName) {
      return tabName.charAt(0).toUpperCase() + tabName.slice(1);
    }
    return 'Navigation';
  }

  getTabId(item: Action, index: number): string {
    const key =
      item.key ||
      item.label?.toLowerCase().replace(/\s+/g, '-') ||
      `tab-${index}`;
    return `tab-${key}`;
  }

  getTabPanelId(item: Action, index: number): string {
    const key =
      item.key ||
      item.label?.toLowerCase().replace(/\s+/g, '-') ||
      `tab-${index}`;
    return `tabpanel-${key}`;
  }

  isTabActive(item: Action): boolean {
    const currentUrl = this.router.url;
    const routerLink = item.routerLink || '';

    // For home tab, use exact matching (same as routerLinkActiveOptions)
    if (item.key === 'home') {
      const normalizedUrl =
        currentUrl === '/' ? '' : currentUrl.replace(/^\//, '');
      return normalizedUrl === '';
    }

    // For other tabs, check if current URL starts with the routerLink
    if (typeof routerLink === 'string') {
      const normalizedRouterLink =
        routerLink === '' ? '' : routerLink.replace(/^\//, '');
      const normalizedUrl = currentUrl.replace(/^\//, '');

      if (normalizedRouterLink === '') {
        return normalizedUrl === '';
      }

      return (
        normalizedUrl === normalizedRouterLink ||
        normalizedUrl.startsWith(`${normalizedRouterLink}/`)
      );
    }

    return false;
  }
}
